import { useEffect, useMemo } from 'react';
import { ProgramCard, ProgramListFilter } from '@widgets/programs/index.ts';
import type { ProgramListFilterProps } from '@widgets/programs/list-filter/ui.tsx';
import { GridProgramCard } from '@widgets/programs/program-card/grid-program-card/grid-program-card.tsx';
import { InfinityList } from '@widgets/shared/InfinityList/index.tsx';
import { BodyWithFilterAndList } from '@features/common/BodyWithFilterAndList/index.tsx';
import { useMobile } from '@shared/hooks/index.ts';
import { useProgramsListViewType } from '@shared/hooks/use-programs-list-view-type.ts';
import type { CachedListData } from '@shared/hooks/use-save-scroll.ts';
import type { ActiveTabType } from '@shared/types/common';
import { SkeletonGridList, SkeletonList } from '@shared/ui/skeleton-list/index.ts';
import { useProgramsInfiniteQuery } from '@src/api/api-hooks/programs/useProgramsInfiniteQuery.ts';
import { Blank } from '@src/library/components/common/index.ts';
import * as st from './styles.module.scss';

type PT = {
  filterData: ProgramListFilterProps;
  tabName?: ActiveTabType;
  params: Record<string, any>;
  setTitles?: (titles: string[]) => void;
  doSetStorageData: (data: CachedListData) => void;
};

export const ProgramsListBody = ({
  filterData,
  doSetStorageData,
  tabName,
  params,
  setTitles,
}: PT) => {
  const { isListView } = useProgramsListViewType({ activeTab: tabName });
  const { isMobile } = useMobile();
  const {
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    data: programsData,
    fetchNextPage,
  } = useProgramsInfiniteQuery({
    ...params,
    tabName,
  });

  const programsList = useMemo(() => programsData?.results || [], [programsData]);

  useEffect(() => {
    const titles = programsList.map((p) => p.title);
    setTitles && setTitles(titles);
  }, [programsList, setTitles]);

  const handleClickCard = () => {
    doSetStorageData({
      scrollY: window.scrollY,
    });
  };

  return (
    <BodyWithFilterAndList
      isFilterOpen={filterData.isOpen}
      Filter={<ProgramListFilter {...filterData} />}
      List={
        <InfinityList
          className={isListView ? st.list : st.gridList}
          loading={isFetchingNextPage}
          loadMore={fetchNextPage}
          hasMore={hasNextPage}
        >
          {isLoading ? (
            isListView ? (
              <SkeletonList />
            ) : (
              <SkeletonGridList />
            )
          ) : (
            programsList.map((program, index) =>
              isListView ? (
                <ProgramCard
                  activeTab={tabName}
                  needDropDawnShowUp={
                    programsList.length - 1 === index &&
                    (isMobile ? programsList.length > 1 : programsList.length > 6)
                  }
                  key={program.pk}
                  program={program}
                  onClick={handleClickCard}
                />
              ) : (
                <GridProgramCard
                  activeTab={tabName}
                  needDropDawnShowUp={false}
                  key={program.pk}
                  program={program}
                  onClick={handleClickCard}
                />
              )
            )
          )}
        </InfinityList>
      }
      EmptyBlank={
        <Blank
          text={
            params.search || params.tags?.length
              ? 'По вашему запросу ничего не найдено'
              : 'В этом списке ничего нет'
          }
        />
      }
      isShowBlank={programsList.length === 0 && !isLoading}
    />
  );
};
